'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronsUpDown } from 'lucide-react';
import { useProjects } from '../hooks/use-projects';
import { useNewProjectStore } from '../model/new-project-store';
import { ProjectListMenu } from './ProjectListMenu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { cn } from '@/shared/lib/cn';

interface ProjectSwitcherProps {
  currentProjectId?: string;
  currentProjectName?: string;
  className?: string;
  // Center the title horizontally (onboarding empty state). Left flag by
  // default, where the title sits above left-aligned content cards.
  isCentered?: boolean;
}

/**
 * Switches between the user's buildings from the project page hero: the large
 * title itself doubles as the switcher (building name + up/down chevron, no
 * leading icon), mirroring the Apple hero switcher. The switching affordance
 * (chevron / popover) only appears when there is more than one building — with
 * a single building there is nothing to switch to, so we show a plain title.
 * New buildings are created from the popover or the project header ⋮ menu.
 */
export function ProjectSwitcher({
  currentProjectId,
  currentProjectName,
  className,
  isCentered = false,
}: ProjectSwitcherProps) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const openNewProject = useNewProjectStore((s) => s.open);
  const { data: projectsResponse } = useProjects();
  const projects = projectsResponse?.data || [];
  const hasMultiple = projects.length > 1;

  const handleProjectSelect = (projectId: string) => {
    setOpen(false);
    router.push(`/project/${projectId}`);
  };

  const handleCreate = () => {
    setOpen(false);
    openNewProject();
  };

  // Split layout (margins) from typography. The hero type role lives on the
  // text-bearing element itself, NOT inherited down from the <h1>: a nested
  // <button> would otherwise depend on Preflight's `font: inherit` reset to
  // pick up the 45px size, which is fragile (and silently regressed the hero
  // to the UA default button size). Applying the role directly keeps the
  // switcher title the same size as the plain title.
  const layoutClass = 'mt-8 pb-6';
  const typographyClass =
    'text-screen-hero-title tracking-tight text-foreground';
  if (!hasMultiple) {
    return (
      <h1
        className={cn(
          layoutClass,
          typographyClass,
          isCentered && 'text-center',
          className
        )}
      >
        {currentProjectName || 'Select building'}
      </h1>
    );
  }
  return (
    // <button> nested in <h1> keeps the heading semantic while making the
    // title the menu trigger; the hero type role is applied to the button so
    // it never relies on font inheritance.
    <h1 className={cn(layoutClass, isCentered && 'flex justify-center', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex items-center gap-2 max-w-full',
              isCentered ? 'text-center' : 'text-start',
              typographyClass
            )}
            aria-label={currentProjectName || 'Switch building'}
          >
            <span className="truncate">
              {currentProjectName || 'Select building'}
            </span>
            <ChevronsUpDown className="h-7 w-7 flex-shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1" align="start">
          <ProjectListMenu
            projects={projects}
            currentProjectId={currentProjectId}
            onSelect={handleProjectSelect}
            onCreate={handleCreate}
          />
        </PopoverContent>
      </Popover>
    </h1>
  );
}
